/**
 * Web Crypto API Utilities
 *
 * Helper functions for cryptographic operations using the browser's native Web Crypto API. All operations are
 * asynchronous and use modern crypto standards.
 *
 * Browser Compatibility:
 *
 * - Chrome 90+
 * - Firefox 88+
 * - Safari 14+
 * - Edge 90+
 *
 * @packageDocumentation
 */

import { ENCRYPTION_CONFIG } from '../models/impersonation-token.model';

/**
 * Generates cryptographically secure random bytes
 *
 * @example
 *   ```typescript
 *   const iv = generateRandomBytes(12); // 12-byte IV for AES-GCM
 *   const salt = generateRandomBytes(16); // 16-byte salt for PBKDF2
 *   ```;
 *
 * @param length - Number of bytes to generate
 * @returns Uint8Array of random bytes
 */
export function generateRandomBytes(length: number): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(length));
}

/**
 * Converts ArrayBuffer to Base64 string
 *
 * @param buffer - ArrayBuffer to convert
 * @returns Base64-encoded string
 */
export function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Converts Base64 string to ArrayBuffer
 *
 * @param base64 - Base64-encoded string
 * @returns ArrayBuffer
 */
export function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Converts string to UTF-8 encoded ArrayBuffer
 *
 * @param str - String to convert
 * @returns ArrayBuffer with UTF-8 encoded string
 */
export function stringToArrayBuffer(str: string): ArrayBuffer {
  const encoder = new TextEncoder();
  return encoder.encode(str).buffer;
}

/**
 * Converts ArrayBuffer to UTF-8 decoded string
 *
 * @param buffer - ArrayBuffer to convert
 * @returns Decoded string
 */
export function arrayBufferToString(buffer: ArrayBuffer): string {
  const decoder = new TextDecoder();
  return decoder.decode(buffer);
}

/**
 * Derives a cryptographic key from a password/passphrase using PBKDF2
 *
 * This is the core key derivation function used for AES encryption. It uses the session token ID as the password and a
 * random salt.
 *
 * @example
 *   ```typescript
 *   const sessionId = 'b0aa6791-3ce7-4943-8a07-6e03bfc3761e';
 *   const salt = generateRandomBytes(16);
 *   const key = await deriveKey(sessionId, salt, 100000, 256);
 *   ```;
 *
 * @param password - Password/passphrase to derive key from (typically session token ID)
 * @param salt - Random salt (16 bytes recommended)
 * @param iterations - PBKDF2 iteration count (100,000+ recommended for 2024)
 * @param keyLength - Key length in bits (256 for AES-256)
 * @returns CryptoKey suitable for AES-GCM encryption/decryption
 */
export async function deriveKey(
  password: string,
  salt: Uint8Array,
  iterations: number = ENCRYPTION_CONFIG.PBKDF2_ITERATIONS,
  keyLength: number = ENCRYPTION_CONFIG.KEY_LENGTH
): Promise<CryptoKey> {
  // Import password as a key material
  const passwordBuffer = stringToArrayBuffer(password);
  const passwordKey = await crypto.subtle.importKey('raw', passwordBuffer, 'PBKDF2', false, [
    'deriveBits',
    'deriveKey',
  ]);

  // Derive AES key from password using PBKDF2
  const key = await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt as BufferSource,
      iterations: iterations,
      hash: ENCRYPTION_CONFIG.PBKDF2_HASH,
    },
    passwordKey,
    {
      name: ENCRYPTION_CONFIG.ALGORITHM,
      length: keyLength,
    },
    false, // Not extractable (more secure)
    ['encrypt', 'decrypt']
  );

  return key;
}

/**
 * Encrypts data using AES-GCM
 *
 * @example
 *   ```typescript
 *   const encrypted = await encryptData('secret message', key, iv);
 *   ```;
 *
 * @param data - Data to encrypt (UTF-8 string)
 * @param key - CryptoKey for encryption
 * @param iv - Initialization vector (12 bytes recommended for AES-GCM)
 * @returns Encrypted data as ArrayBuffer (includes authentication tag)
 */
export async function encryptData(data: string, key: CryptoKey, iv: Uint8Array): Promise<ArrayBuffer> {
  const dataBuffer = stringToArrayBuffer(data);

  const encrypted = await crypto.subtle.encrypt(
    {
      name: ENCRYPTION_CONFIG.ALGORITHM,
      iv: iv as BufferSource,
      tagLength: ENCRYPTION_CONFIG.TAG_LENGTH * 8, // Convert bytes to bits
    },
    key,
    dataBuffer
  );

  return encrypted;
}

/**
 * Decrypts data using AES-GCM
 *
 * @example
 *   ```typescript
 *   const decrypted = await decryptData(encrypted, key, iv);
 *   ```;
 *
 * @param encryptedData - Encrypted data (includes authentication tag)
 * @param key - CryptoKey for decryption
 * @param iv - Initialization vector (same as used for encryption)
 * @returns Decrypted data as UTF-8 string
 * @throws Error if decryption fails (wrong key, tampered data, etc.)
 */
export async function decryptData(encryptedData: ArrayBuffer, key: CryptoKey, iv: Uint8Array): Promise<string> {
  const decrypted = await crypto.subtle.decrypt(
    {
      name: ENCRYPTION_CONFIG.ALGORITHM,
      iv: iv as BufferSource,
      tagLength: ENCRYPTION_CONFIG.TAG_LENGTH * 8, // Convert bytes to bits
    },
    key,
    encryptedData
  );

  return arrayBufferToString(decrypted);
}

/**
 * Checks if Web Crypto API is available in the current browser
 *
 * @returns True if Web Crypto API is supported
 */
export function isWebCryptoSupported(): boolean {
  return (
    typeof crypto !== 'undefined' &&
    typeof crypto.subtle !== 'undefined' &&
    typeof crypto.getRandomValues === 'function'
  );
}

/**
 * Constant-time comparison of two strings
 *
 * Prevents timing attacks when comparing sensitive values. Note: This is a best-effort implementation in JavaScript.
 *
 * @param a - First string
 * @param b - Second string
 * @returns True if strings are equal
 */
export function constantTimeCompare(a: string, b: string): boolean {
  if (a.length !== b.length) {
    return false;
  }

  let result = 0;
  for (let i = 0; i < a.length; i++) {
    result |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }

  return result === 0;
}
